import type { AddClassMembersInput } from '@repo/validation';

/**
 * THI-267 Task 13 — the confirm table's own draft row shape, shared by the
 * "Tạo mới" and "Chọn từ danh sách" tabs (the Excel tab is a different
 * confirm surface, `PreviewStep`, and does not go through this file). Pure
 * client-side state; `buildAddClassMemberRows` below is the ONE place that
 * turns it into `POST /api/classes/:id/members`'s `rows` payload, so the
 * confirm table's own footer count and the actual request can never diverge
 * (same shape of guarantee as the Excel wizard's `includedRows`/
 * `buildCommitRows` in `pages/students/import/types.ts`).
 */
export type PayerDraft =
  | { mode: 'existingClient'; clientId: string }
  | { mode: 'newPayer'; name: string; phone: string | null }
  | { mode: 'self' };

export interface ExistingEnrollmentInfo {
  enrollmentId: string;
  pricingType: 'PER_SESSION' | 'MONTHLY_FLAT';
  rate: number;
  /** A PAUSED enrollment in the class's subject: the row is EXCLUDED from
   *  the batch entirely (spec resolutions — "its row is excluded"), never
   *  sent as KEEP/REPRICE. `buildAddClassMemberRows` enforces this; the row
   *  still renders in the table with the "Đang tạm dừng…" note. */
  paused: boolean;
}

export interface DraftMemberRow {
  /** Client-only React key / edit-state key — never sent to the API. */
  key: string;
  /** Set for a row that resolved to (or was picked as) an existing student —
   *  "Chọn từ danh sách", or "Tạo mới" with a typed-duplicate match kept as
   *  "Dùng học viên đã có". Null for a brand-new student. */
  studentId: string | null;
  studentName: string;
  /** Set only for a brand-new student row (studentId null). */
  newStudent: { nickname: string | null; dateOfBirth: string | null; payer: PayerDraft } | null;
  /** The student's live ACTIVE (or PAUSED) enrollment in the class's own
   *  subject, if any — drives the KEEP/REPRICE choice, the locked-rate note
   *  on a pricing-type mismatch, and the PAUSED exclusion above. Null for a
   *  brand-new student (nothing to keep or reprice). */
  existingEnrollment: ExistingEnrollmentInfo | null;
  /** Raw text input for the Học phí cell. Blank means "fall back to the
   *  class default, then the subject default" — never sent as 0. */
  rate: string;
  /** The user opened "Sửa học phí" and is changing an EXISTING enrollment's
   *  price — only meaningful when `existingEnrollment` is set. Irrelevant
   *  (and ignored) for a brand-new enrollment, which is always a plain
   *  create regardless of this flag. */
  repriceRequested: boolean;
  /** Explicit khung giờ subset, or null for "every class slot" (the
   *  default — every chip starts ticked). An explicit array equal to every
   *  class slot id is equivalent to null on the wire (both omit `slotIds`,
   *  letting the server default to all of them) but is kept distinct here so
   *  the UI can tell "never touched" from "user ticked them all back". */
  slotIds: string[] | null;
}

export interface BuildAddClassMemberRowsResult {
  rows: AddClassMembersInput['rows'];
  /** studentIds shared by more than one row that would otherwise be
   *  submitted — the server rejects this as `DUPLICATE_ROW` on the first
   *  offending index, so the confirm table flags it and blocks submit
   *  before the request ever goes out. */
  duplicateStudentIds: string[];
  /** Draft keys left out of `rows` because their existing enrollment is
   *  PAUSED. */
  excludedPausedKeys: string[];
  /** `DraftMemberRow.key`, in the SAME order as `rows` — a server 400's
   *  `rowIndex` (0-based, into the request's `rows` array) is looked up
   *  against this to find which draft row to mark, since the request array
   *  and the full draft list diverge whenever a PAUSED row was excluded. */
  includedKeys: string[];
}

/** Parses the Học phí cell's raw text into a positive integer, or undefined
 *  when blank/zero/unparseable — "blank falls back to the class default,
 *  then the subject default" (never a 0 the server would reject with
 *  RATE_REQUIRED, and never NaN). Strips everything but digits so "1.200.000"
 *  and "1200000" parse the same, matching `parseVndInput` (lib/formatVnd.ts)
 *  elsewhere in the app. */
export function parseRateInput(raw: string): number | undefined {
  const digits = raw.trim().replace(/[^\d]/g, '');
  if (!digits) return undefined;
  const n = Number(digits);
  return n > 0 ? n : undefined;
}

function toPayerInput(payer: PayerDraft): { clientId: string } | { name: string; phone: string | null } | 'SELF' {
  if (payer.mode === 'self') return 'SELF';
  if (payer.mode === 'existingClient') return { clientId: payer.clientId };
  return { name: payer.name, phone: payer.phone };
}

/**
 * Derives the exact `POST /api/classes/:id/members` `rows` payload from the
 * confirm table's draft rows, plus the duplicate/PAUSED bookkeeping the
 * table needs to render its own warnings before submit.
 *
 * `allSlotIds` is the class's full khung giờ set (`ClassGroupDetail.slots`
 * mapped to id) — needed only to decide whether a row's `slotIds` equals
 * "every slot" and can therefore be omitted from the wire payload (the
 * server's own default).
 */
export function buildAddClassMemberRows(
  drafts: DraftMemberRow[],
  allSlotIds: string[],
): BuildAddClassMemberRowsResult {
  const excludedPausedKeys = drafts.filter((d) => d.existingEnrollment?.paused).map((d) => d.key);
  const included = drafts.filter((d) => !d.existingEnrollment?.paused);

  const seen = new Map<string, number>();
  for (const d of included) {
    if (!d.studentId) continue;
    seen.set(d.studentId, (seen.get(d.studentId) ?? 0) + 1);
  }
  const duplicateStudentIds = [...seen.entries()].filter(([, count]) => count > 1).map(([id]) => id);

  const rows = included.map((d) => {
    const rate = parseRateInput(d.rate);
    // A subset only when it's genuinely smaller than the full set — null
    // ("never touched") and "every id present" both mean the same thing on
    // the wire: omit `slotIds` and let the server default to all of them.
    const isSubset = d.slotIds !== null && d.slotIds.length < allSlotIds.length;

    const extra: { rate?: number; slotIds?: string[]; existingEnrollmentAction?: 'KEEP' | 'REPRICE' } = {};
    if (rate !== undefined) extra.rate = rate;
    if (isSubset) extra.slotIds = d.slotIds!;
    if (d.existingEnrollment) extra.existingEnrollmentAction = d.repriceRequested ? 'REPRICE' : 'KEEP';

    if (d.studentId) {
      return { studentId: d.studentId, ...extra };
    }
    const ns = d.newStudent!;
    return {
      newStudent: {
        name: d.studentName,
        nickname: ns.nickname,
        dateOfBirth: ns.dateOfBirth,
        payer: toPayerInput(ns.payer),
      },
      ...extra,
    };
  }) as AddClassMembersInput['rows'];

  return { rows, duplicateStudentIds, excludedPausedKeys, includedKeys: included.map((d) => d.key) };
}
